import { Suspense, useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar";
import { BlobBackground } from "@/pages/onboarding/BlobBackground";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageTransition } from "@/components/layout/PageTransition";
import { ContentSkeleton } from "@/components/layout/ContentSkeleton";
import { PageChromeProvider } from "@/components/layout/PageChrome";
import { useNavTree } from "@/components/sidebar/hooks/useNavTree";
import { isScrollableRoute } from "@/constants/scrollableRoutes";

/**
 * Persistent shell for every protected page. The sidebar and header mount here
 * once and survive navigation; only the Outlet swaps. The Suspense boundary is
 * inside the shell so a lazy page chunk shows a content skeleton rather than
 * blanking the sidebar and header.
 *
 * Geometry is the Home Shell design's: a white ground carries the white rail
 * on the left, and the routed content sits on a grey sheet inset 9px from the
 * top with a hairline top/left border and a rounded top-left corner — the
 * sheet, not a sidebar border, is what separates the two. On the home lobby
 * the ambient blob wash paints inside the sheet, clipped by its corner.
 *
 * The nav tree is built once here and handed to both the sidebar and the
 * breadcrumb header, so the two can never disagree and the tree is not
 * recomputed per surface.
 */
export function AppShell() {
  const { pathname } = useLocation();
  const sections = useNavTree();
  const overflowClass = isScrollableRoute(pathname) ? "overflow-y-auto" : "overflow-hidden";
  const isHomeLobby = pathname === "/ui";

  const mainRef = useRef<HTMLElement>(null);
  const isFirstRender = useRef(true);

  // On navigation, hand focus to the content region and reset its scroll, so a
  // keyboard user lands in the new page rather than on the link they clicked and
  // the page doesn't open half-scrolled. Skipped on first paint, and never steals
  // focus a page has already placed inside itself (the chat composer autofocuses).
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const main = mainRef.current;
    if (!main) return;
    main.scrollTop = 0;
    if (!main.contains(document.activeElement)) {
      main.focus({ preventScroll: true });
    }
  }, [pathname]);

  return (
    <PageChromeProvider>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded-v2-md focus:bg-v2-background focus:px-3 focus:py-2 focus:text-v2-sm focus:text-v2-foreground focus:shadow-md focus:outline-none focus:ring-2 focus:ring-v2-ring"
      >
        Skip to content
      </a>
      <div className="flex h-screen bg-white">
        <AppSidebar sections={sections} />
        {/* The content sheet. `isolate` + `overflow-hidden` clip the blob wash
            and page content to the rounded corner; the hairline is the
            design's rgba(0,0,0,.045) on a #F7F7F7 sheet. */}
        <div className="relative isolate mt-[9px] flex min-w-0 flex-1 flex-col overflow-hidden rounded-tl-[10px] border-l border-t border-black/[0.045] bg-[#F7F7F7]">
          {isHomeLobby && <BlobBackground position="absolute" />}
          <PageHeader sections={sections} />
          <main
            id="main-content"
            ref={mainRef}
            tabIndex={-1}
            className={`relative flex min-h-0 flex-1 flex-col outline-none ${overflowClass}`}
          >
            <PageTransition>
              <Suspense fallback={<ContentSkeleton />}>
                <Outlet />
              </Suspense>
            </PageTransition>
          </main>
        </div>
      </div>
    </PageChromeProvider>
  );
}
